import { ensureThemeJsonArray, getDvSourceImageUrl } from "../../functions/themeAssets";

export const RACE_DEFAULT_SPEED = 6;
export const RACE_DEFAULT_QUESTION_ON_OBSTACLE = 2;
export const RACE_DEFAULT_MAX_HITS = 3;
export const RACE_OBSTACLE_COUNT = 3;

/** Nitro: charge meter — toggle on/off; drains while active; fills on obstacle pass. */
export const NITRO_MAX = 100;
export const NITRO_START = 100;
/** Drain % per second while nitro is engaged. */
export const NITRO_DRAIN_PER_SEC = 22;
/** Slow refill while nitro is off (live increase). */
export const NITRO_REGEN_PER_SEC = 4;
/** Charge gained each time an obstacle is cleared. */
export const NITRO_GAIN_ON_OBSTACLE = 14;
export const NITRO_SPEED_MULTIPLIER = 1.75;
/** @deprecated kept for any leftover duration-based callers */
export const NITRO_DURATION_MS = 3500;

/** Endless scoring / difficulty ramps (medium defaults). */
export const ENDLESS_DISTANCE_MULTIPLIER = 0.05;
export const ENDLESS_SPEED_RAMP_PER_SEC = 0.085;
export const ENDLESS_MAX_SPEED_BONUS = 10;
export const ENDLESS_SPAWN_BASE = 350;
export const ENDLESS_SPAWN_MIN = 140;
export const ENDLESS_SPAWN_RAMP_PER_SEC = 6;

/**
 * Per-difficulty endless feel. Medium vs hard used to share spawn density
 * and on-screen closing-speed caps, so they played almost the same.
 */
export const ENDLESS_DIFFICULTY = {
  easy: {
    speed: 4.2,
    rampPerSec: 0.035,
    maxBonus: 3.5,
    spawnBase: 450,
    spawnMin: 220,
    spawnRamp: 2.8,
    obstacleCount: 3,
    trafficMin: 0.38,
    trafficMax: 0.7,
    staticChance: 0.14,
    approachSec: 1.9,
    approachFloor: 1.5,
    nitroMul: 1.9,
    steerLerp: 0.3,
    steerMul: 1.12,
  },
  medium: {
    speed: 6.2,
    rampPerSec: 0.065,
    maxBonus: 6.5,
    spawnBase: 380,
    spawnMin: 175,
    spawnRamp: 4.6,
    obstacleCount: 4,
    trafficMin: 0.5,
    trafficMax: 0.85,
    staticChance: 0.14,
    approachSec: 1.75,
    approachFloor: 1.35,
    nitroMul: 1.75,
    steerLerp: 0.24,
    steerMul: 1,
  },
  hard: {
    speed: 8.6,
    rampPerSec: 0.11,
    maxBonus: 11,
    spawnBase: 280,
    spawnMin: 120,
    spawnRamp: 7.2,
    obstacleCount: 5,
    trafficMin: 0.7,
    trafficMax: 1.08,
    staticChance: 0.3,
    approachSec: 1.2,
    approachFloor: 0.9,
    nitroMul: 1.55,
    steerLerp: 0.18,
    steerMul: 0.8,
  },
};

export function getEndlessDifficultyProfile(difficulty) {
  const id = String(difficulty || "medium").toLowerCase();
  return ENDLESS_DIFFICULTY[id] || ENDLESS_DIFFICULTY.medium;
}

/** Quiz wrong-answer penalty. */
export const QUIZ_WRONG_SLOW_FACTOR = 0.55;
export const QUIZ_WRONG_SLOW_MS = 2500;

export const GAME_MODE_ENDLESS = "endless_runner";
export const GAME_MODE_QUIZ = "quiz_runner";

export function isEndlessMode(mode) {
  return String(mode || "").trim() === GAME_MODE_ENDLESS;
}

export function parseStringList(value) {
  if (Array.isArray(value)) {
    return value.map((v) => String(v ?? "").trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed)
        ? parsed.map((v) => String(v ?? "").trim()).filter(Boolean)
        : [];
    } catch {
      return value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  return [];
}

export function parseRaceAssetsFromTheme(themeData) {
  const roadFile = String(themeData?.road_image ?? "").trim();
  const carFiles = parseStringList(themeData?.car_images);
  const obstacleFiles = parseStringList(themeData?.obstacle_images);
  const raceSoundFile = String(
    themeData?.race_sound ?? themeData?.car_race_sound ?? ""
  ).trim();

  return {
    roadUrl: roadFile ? getDvSourceImageUrl(roadFile) : "",
    carUrls: carFiles.map((f) => getDvSourceImageUrl(f)).filter(Boolean),
    obstacleUrls: obstacleFiles.map((f) => getDvSourceImageUrl(f)).filter(Boolean),
    logoUrl: themeData?.logo ? getDvSourceImageUrl(themeData.logo) : "",
    raceSoundUrl: raceSoundFile ? getDvSourceImageUrl(raceSoundFile) : "",
  };
}

export function parseQuizFromTheme(themeData) {
  return ensureThemeJsonArray(themeData?.quiz)
    .map((item) => ({
      question_name: String(item?.question_name ?? item?.question ?? "").trim(),
      option_one: String(item?.option_one ?? "").trim(),
      option_two: String(item?.option_two ?? "").trim(),
      option_three: String(item?.option_three ?? "").trim(),
      option_four: String(item?.option_four ?? "").trim(),
      correct_answer: String(item?.correct_answer ?? "").trim(),
    }))
    .filter((q) => {
      if (!q.question_name || !q.correct_answer || !q.option_one || !q.option_two) {
        return false;
      }
      const options = [q.option_one, q.option_two, q.option_three, q.option_four].filter(Boolean);
      if (options.length < 2) return false;
      // Reject gaps (e.g. option_four filled but option_three empty)
      if (q.option_four && !q.option_three) return false;
      const answer = q.correct_answer.toLowerCase();
      return options.some((opt) => opt.toLowerCase() === answer);
    });
}

export function getRaceMaxHits(themeData) {
  const n = Number(themeData?.game_end ?? themeData?.max_hits ?? RACE_DEFAULT_MAX_HITS);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : RACE_DEFAULT_MAX_HITS;
}

export function isThemeFlagOn(val, defaultOn = false) {
  if (val == null || val === "") return defaultOn;
  return String(val).toLowerCase() === "true";
}

/** Hits allowed in a single endless run. Always 1 — a crash ends that attempt. */
export function getEndlessMaxHits() {
  return 1;
}

export function isEndlessLifelineOn(themeData) {
  return isThemeFlagOn(themeData?.endless_lifeline);
}

/**
 * How many endless runs the player may take when lifeline is on.
 * 0 = unlimited (toggle off).
 */
export function getEndlessLifelinePlays(themeData) {
  if (!isEndlessLifelineOn(themeData)) return 0;
  const n = Number(themeData?.endless_lifeline_count);
  if (!Number.isFinite(n) || n < 1) return 3;
  return Math.min(20, Math.trunc(n));
}

/** How many obstacles the player must cross before a quiz question appears. */
export function getQuestionOnObstacle(themeData) {
  const n = Number(
    themeData?.question_on_obstacle ?? RACE_DEFAULT_QUESTION_ON_OBSTACLE
  );
  return Number.isFinite(n) && n > 0
    ? Math.trunc(n)
    : RACE_DEFAULT_QUESTION_ON_OBSTACLE;
}

export function getObstaclePoints(themeData) {
  const n = Number(themeData?.obstacle_points ?? 1);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 1;
}

export function getQuizPoints(themeData) {
  const raw =
    themeData?.points ??
    themeData?.point ??
    themeData?.quiz_points ??
    themeData?.right_points ??
    1;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 1;
}

/** Optional: also open quiz after this many points since last question (0 = disabled). */
export function getQuestionOnPoints(themeData) {
  const n = Number(themeData?.question_on_points ?? 0);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

export function getDistanceMultiplier(themeData) {
  const n = Number(
    themeData?.distance_multiplier ?? ENDLESS_DISTANCE_MULTIPLIER
  );
  return Number.isFinite(n) && n > 0 ? n : ENDLESS_DISTANCE_MULTIPLIER;
}

export function computeEndlessScore({
  obstaclesAvoided,
  distanceTravelled,
  pointsPerObstacle,
  distanceMultiplier,
}) {
  const obs = Math.max(0, Math.floor(Number(obstaclesAvoided) || 0));
  const dist = Math.max(0, Number(distanceTravelled) || 0);
  const perObs = Math.max(0, Number(pointsPerObstacle) || 0);
  const distMul = Math.max(0, Number(distanceMultiplier) || 0);
  return Math.max(0, Math.floor(obs * perObs + dist * distMul));
}

export function isCollide(a, b) {
  if (!a || !b) return false;
  const aRect = a.getBoundingClientRect();
  const bRect = b.getBoundingClientRect();
  return !(
    aRect.bottom < bRect.top ||
    aRect.top > bRect.bottom ||
    aRect.right < bRect.left ||
    aRect.left > bRect.right
  );
}

export function pickRandom(list) {
  if (!list?.length) return "";
  return list[Math.floor(Math.random() * list.length)];
}
